import { Injectable } from '@angular/core';
import { ConfigurationService } from '@draeger-academy/common/lib/services/configuration/configuration.service';
import { CookieConsentOverlayService } from '@draeger-academy/presentation';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Observable } from 'rxjs/internal/Observable';
import { LoadScriptService } from '../load-script/load-script.service';
import { TrackingPageTemplateEnum } from './shared/enums/template';
import { TrackEventInterface } from './shared/interfaces/track-event';
import { TrackPageInterface, TrackPageProductInterface, TrackPageSearchInterface } from './shared/interfaces/track-page';

@Injectable()
export class TrackingV2Service {
    private readonly _trackingScriptUrl = 'https://42.draeger.com/global/dte.js';
    private _trackingScriptState: undefined | 'loading' | 'loaded' | 'error';
    private _queuedTracking = [];
    private _breadcrumb: string[];
    private _country: string;
    private _language: string;
    private _youTubeTrackingEnabled: boolean;

    private _$trackingScriptLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public get trackingScriptLoaded$(): Observable<any> {
        return this._$trackingScriptLoaded.asObservable();
    }

    constructor(private _loadScriptService: LoadScriptService, private _configurationService: ConfigurationService) {
        window['dte_statistic'] = window['dte_statistic'] || [];
    }

    public loadTrackingScript(): void {
        // tslint:disable-next-line:max-line-length
        if (!this._trackingScriptState && sessionStorage.getItem('trackingScript') !== 'loaded') {
            this._trackingScriptState = 'loading';
            this._loadScriptService
                .load('TrackingScript', this._trackingScriptUrl)
                .then((message) => {
                    this._trackingScriptState = 'loaded';
                    this._$trackingScriptLoaded.next(true);
                    this._catchUpTracking();
                    sessionStorage.setItem('trackingScript', 'loaded');
                })
                .catch((error) => {
                    this._trackingScriptState = 'error';
                });
        }
    }

    public trackEvent(eventTrackingObject: TrackEventInterface): void {
        this._track(eventTrackingObject);
    }

    public trackPage(pageTrackingData: {
        template: string;
        title: string;
        products?: TrackPageProductInterface[];
        search?: TrackPageSearchInterface;
    }): void {
        let _site = pageTrackingData.template === 'CountrySelectionPage' ? 'en_ww' : this._configurationService.getCurrentSite();
        if (this._configurationService.isPartnerPage()) {
            const portalCountryCode = localStorage.getItem('portalCountryCode');
            if (portalCountryCode) {
                _site = JSON.parse(portalCountryCode).countryCode;
            }
        }
        this._setCountryLanguageV2(_site);
        const _pageTrackingObject: TrackPageInterface = {
            action: 'page.ready',
            data: {
                page: {
                    country: this._country === 'uk' ? 'gb' : this._country,
                    language: this._language,
                    segment: 'Unsegmented',
                    title: pageTrackingData.title,
                    template: pageTrackingData.template,
                    breadcrumb: this._breadcrumb
                },
                products: pageTrackingData.products,
                search: pageTrackingData.search
            }
        };
        this._track(_pageTrackingObject);
    }

    private _setCountryLanguageV2(site: string): void {
        if (site.length > 0) {
            const _countryLanguage = site.split('_');
            this._language = _countryLanguage[0].toLowerCase();
            this._country = _countryLanguage[1].toLowerCase();
        }
    }

    public enableYouTubeTracking(): void {
        if (!this._youTubeTrackingEnabled) {
            this._youTubeTrackingEnabled = true;
            window['dte_statistic'].push({
                action: 'youtube.enable'
            });
        }
    }

    private _track(trackingObject: TrackEventInterface | TrackPageInterface): void {
        if (sessionStorage.getItem('trackingScript') === 'loaded') {
            if (window) {
                window['dte_statistic'].push(trackingObject);
            }
        } else {
            this._queuedTracking.push(trackingObject);
        }
    }

    private _catchUpTracking(): void {
        for (const _queuedTrackingEntry of this._queuedTracking) {
            window['dte_statistic'].push(_queuedTrackingEntry);
        }
        this._queuedTracking = undefined;
    }

    public setBreadcrumb(breadcrumb: string[]): void {
        this._breadcrumb = breadcrumb;
    }
}
